import { api, ApiError } from "../api.js";
import { esc } from "../utils.js";
import { navigate } from "../app.js";
import { confirmAction } from "../confirmDialog.js";

/** Doubles as both "create a new community" (#/create-community) and
 * "edit an existing one" (#/community/:id/edit) — params.id, when
 * present, switches this into edit mode and pre-fills the form. */
export async function render(container, params) {
  const editingId = params?.id;
  let existing = null;

  if (editingId) {
    container.innerHTML = `<div class="text-center py-5"><div class="spinner-border text-primary"></div></div>`;
    try {
      existing = await api.get(`/cohorts/${editingId}`);
    } catch (err) {
      container.innerHTML = `<div class="dc-empty"><i class="bi bi-exclamation-triangle"></i><div>Cohort not found.</div></div>`;
      return;
    }
  }

  container.innerHTML = `
    <h1 class="display-heading h3 mb-4">${editingId ? "Edit Cohort" : "New Cohort"}</h1>
    <form id="community-form">
      <div class="mb-3">
        <label class="form-label" for="community-name">Cohort name</label>
        <input type="text" class="form-control" id="community-name" required autofocus
          value="${existing ? esc(existing.name) : ""}" />
      </div>
      <div class="mb-3">
        <label class="form-label" for="community-description">Description (optional)</label>
        <textarea class="form-control" id="community-description" rows="4">${existing ? esc(existing.description || "") : ""}</textarea>
      </div>
      <div class="form-check form-switch mb-1">
        <input class="form-check-input" type="checkbox" role="switch" id="community-private"
          ${existing?.is_private ? "checked" : ""} />
        <label class="form-check-label" for="community-private"><i class="bi bi-lock me-1"></i>Private cohort</label>
      </div>
      <div class="form-text mb-3">Joined by invitation from its admins only. Its posts, dive events, photos and
        members are visible to its members only; anyone can see its name and description.</div>
      <div id="community-error" class="text-danger small mb-3 d-none"></div>
      <div class="d-flex gap-2">
        <button type="submit" class="btn btn-primary" id="community-submit">${editingId ? "Save" : "Create"}</button>
        <button type="button" class="btn btn-outline-secondary" id="community-cancel">Cancel</button>
      </div>
    </form>`;

  container.querySelector("#community-cancel").addEventListener("click", () => history.back());

  container.querySelector("#community-form").addEventListener("submit", async (e) => {
    e.preventDefault();
    const errorEl = container.querySelector("#community-error");
    errorEl.classList.add("d-none");
    const submitBtn = container.querySelector("#community-submit");
    submitBtn.disabled = true;

    const body = {
      name: container.querySelector("#community-name").value.trim(),
      description: container.querySelector("#community-description").value.trim() || null,
      is_private: container.querySelector("#community-private").checked,
    };
    // Switching an existing cohort changes who sees what's already in it.
    if (existing && body.is_private !== existing.is_private) {
      const ok = await confirmAction(body.is_private
        ? {
            title: "Make this cohort private?",
            body: "Everything already posted in it, its dive events and photos will be visible to its " +
              "members only, and new members will need an invitation.",
            confirmLabel: "Make private",
            variant: "primary",
          }
        : {
            title: "Make this cohort public?",
            body: "Everything posted in it, its dive events and photos will be visible to anyone, and " +
              "anyone will be able to join.",
            confirmLabel: "Make public",
            variant: "danger",
          });
      if (!ok) {
        submitBtn.disabled = false;
        return;
      }
    }

    try {
      const community = editingId
        ? await api.patch(`/cohorts/${editingId}`, body)
        : await api.post("/cohorts", body);
      navigate(`/cohort/${community.id}`);
    } catch (err) {
      errorEl.textContent = err instanceof ApiError ? err.message : "Something went wrong.";
      errorEl.classList.remove("d-none");
    } finally {
      submitBtn.disabled = false;
    }
  });
}
